import EventEmitter from 'events';

export enum wsEvents {
    ping = 'ping',
    disconnect = 'disconnect',
    connect = 'connect',
    message = 'message',
}

export interface IMessage {
    data?: string;
}

enum protocols {
    ws = 'ws',
    wss = 'wss',
}

type protocol = `${protocols}`;

interface ICmd {
    cmd: string;
}

interface IConfig {
    encryption: boolean;
    host: string;
    port: number;
}

export default class WsEmitter extends EventEmitter {
    private readonly DELAY_MESSAGE = 30000;
    private readonly DELAY_INTERVAL = 1000;
    private readonly PING_TIMEOUT = 10000;
    private _status = false;
    private _interval: NodeJS.Timer | null = null;
    private _pingInterval: NodeJS.Timer | null = null;
    private _socket: WebSocket | null;
    private _lastTimePing: number;
    private _last_time_reconnect: number;
    private url: string;

    constructor(
        private readonly config: IConfig,
        private readonly pingMessage: ICmd,
    ) {
        super();
        this.url = this._getUrl(this.config);
        const currentDate = new Date().getTime();
        this._lastTimePing = currentDate;
        this._last_time_reconnect = currentDate;
    }

    _getPortStr(portNumber: number, protocol: protocol) {
        const httpDefault = 80;
        const httpsDefault = 443;
        const isWsDefault = protocol === 'ws' && portNumber === httpDefault;
        const isWssDefault = protocol === 'wss' && portNumber === httpsDefault;
        return isWsDefault || isWssDefault ? '' : `:${portNumber}`;
    }

    _getUrl(config: IConfig) {
        const { encryption, host, port } = config;
        const protocol = encryption ? 'wss' : 'ws';
        const portStr = this._getPortStr(port, protocol);
        return `${protocol}://${host}${portStr}`;
    }

    _startInterval() {
        if (!this._interval) {
            this._interval = setInterval(() => {
                const currentDate = new Date().getTime();
                const pingTime = currentDate - this._lastTimePing;
                const reconnectTime = currentDate - this._last_time_reconnect;
                if (!this._status || pingTime > this.DELAY_MESSAGE)
                    this.connect();
            }, this.DELAY_INTERVAL);
        }
    }

    _stopInterval() {
        if (this._interval) {
            clearInterval(this._interval);
            this._interval = null;
        }
    }

    _startPing() {
        if (!this._pingInterval) {
            this._pingInterval = setInterval(() => {
                this.sendMessage(this.pingMessage);
            }, this.PING_TIMEOUT);
        }
    }

    _stopPing() {
        if (this._pingInterval) {
            clearInterval(this._pingInterval);
            this._pingInterval = null;
        }
    }

    _handleError() {
        this._status = false;
        this.emit(wsEvents.disconnect);
    }

    _handleClose() {
        this._status = false;
        this.emit(wsEvents.disconnect);
        this._stopPing();
    }

    _handleOpen() {
        this._status = true;
        this.emit(wsEvents.connect);
        this._startPing();
    }

    _handleMessage(message: IMessage | string) {
        this._lastTimePing = new Date().getTime();
        this.emit(wsEvents.message, message);
    }

    connect() {
        try {
            if (this._socket && typeof this._socket.close === 'function') {
                this.close();
            }
            this._socket = new WebSocket(this.url);
            this._status = false;
            const currentDate = new Date().getTime();
            this._last_time_reconnect = currentDate;
            this._lastTimePing - currentDate;
            this._socket.addEventListener('open', this._handleOpen.bind(this));
            this._socket.addEventListener(
                'message',
                this._handleMessage.bind(this),
            );
            this._socket.addEventListener(
                'error',
                this._handleError.bind(this),
            );
            this._socket.addEventListener(
                'close',
                this._handleClose.bind(this),
            );
            if (!this._interval) {
                this._startInterval();
            }
        } catch (e) {}
    }

    close() {
        if (this._socket) {
            this._socket.close();
            this._stopInterval();
            this._socket = null;
        }
    }

    reconnect() {
        this.close();
        this.connect();
    }

    sendMessage(data: ICmd) {
        if (this._socket) {
            let message;
            switch (typeof data) {
                case 'string':
                    message = data;
                    break;
                case 'object':
                    message = JSON.stringify(data);
                    break;
            }
            if (message) this._socket.send(message);
        }
    }
}
